import { Container, Graphics, type Sprite } from 'pixi.js';
import type { EffectClip } from './effectsArt';
import type { ProjectileEffectId } from '../sim/content';
import { PROJECTILE_ART_BASE_ANGLE_RAD } from './arenaFx';
import { UoHueSprite } from './uoHueBatch';

/**
 * The screen-space rotation (radians) a bolt's art should draw at, given its
 * tile-space velocity. UO moving-effect art is authored at one fixed orientation
 * and rotated along the velocity by every classic client (the resolved-cascade
 * tracer `ArenaFxOverlay.projectile` does exactly this). A tile-space delta
 * `(vx, vy)` projects to a screen delta `((vx-vy), (vx+vy))·TILE_HALF` (the
 * `tileToScreen` convention), so the screen heading is `atan2(vx+vy, vx-vy)`; the
 * per-strip authored-orientation offset (`PROJECTILE_ART_BASE_ANGLE_RAD`) is added
 * so each look points nose-first. Pure, so the "which way does the arrow point"
 * decision is unit-tested without a canvas. A zero-velocity bolt yields `0 +
 * offset` (a harmless momentary-stall facing).
 */
export function projectileArtRotation(vx: number, vy: number, look: ProjectileEffectId): number {
  return Math.atan2(vx + vy, vx - vy) + PROJECTILE_ART_BASE_ANGLE_RAD[look];
}

/**
 * The renderer-side sprite for a flying **arena projectile** entity (bullet-hell
 * rework, PRD #399 Slice A). Unlike a mobile it is drawn straight from its
 * replicated position with **no {@link StepAnimator} gait smoothing** - a bolt is
 * not a grid-locked walker, so the renderer just lerps it wherever the snapshot
 * says it is. It is also unlike the dice/moongate/chest props: a projectile is
 * non-interactive (no click, no context menu), a small **centered** billboard
 * rather than a bottom-anchored one, and short-lived (spawned and reaped by the run).
 *
 * The look strip loops the baked projectile effect art ({@link EffectClip}, the
 * `magic-arrow`/`fireball`/`fire-snake` frames). When that art is unbaked (a
 * clientless host whose stubs predate the effect, or a warn-skipped bake) it
 * degrades to a small glowing dot - the effects-art graceful-absence discipline,
 * never a crash.
 */

/** Fallback dot radius (px) when the projectile strip is unbaked. */
const FALLBACK_RADIUS = 4;
/** Fallback bolt colour (a warm magic-arrow orange). */
const FALLBACK_COLOR = 0xffaa33;

export class ProjectileSprite extends Container {
  private readonly clip?: EffectClip;
  private readonly billboard?: Sprite;
  /** Elapsed ms into the looping strip, advanced on the render clock. */
  private elapsedMs = 0;

  /**
   * `clip` is the looping projectile strip (or absent on an unbaked host, which
   * takes the Graphics-dot fallback). A projectile never intercepts pointer
   * events - it is pure motion, so the container stays `eventMode: 'none'`.
   */
  constructor(clip?: EffectClip) {
    super();
    this.eventMode = 'none';
    this.clip = clip && clip.frames.length > 0 ? clip : undefined;

    if (this.clip) {
      const first = this.clip.frames[0]!;
      const billboard = new UoHueSprite();
      billboard.texture = first.texture;
      // Effects draw centered on their frame's baked center (unlike a body clip's
      // foot anchor), so pivot on it and let the container carry the screen point.
      billboard.pivot.set(first.cx, first.cy);
      billboard.eventMode = 'none';
      this.addChild(billboard);
      this.billboard = billboard;
    } else {
      const dot = new Graphics().circle(0, 0, FALLBACK_RADIUS).fill({ color: FALLBACK_COLOR, alpha: 0.9 });
      dot.eventMode = 'none';
      this.addChild(dot);
    }
  }

  /**
   * Orient the bolt along its flight, in **screen-space radians** (the caller
   * derives the heading from the projectile's velocity through the same
   * projection the position uses, then adds the per-strip authored-orientation
   * offset). UO moving-effect art is authored at one fixed angle and rotated
   * along the velocity by every classic client - the resolved-cascade tracer
   * (`ArenaFxOverlay.projectile`) already does exactly this; without it a
   * traveling bolt draws in its baked orientation regardless of where it flies
   * (the "arrow going the wrong way" the straight-shot entity showed). The
   * billboard's center sits on the container origin (its pivot is `cx,cy`), so
   * rotating the container spins the art about its own center; the fallback dot
   * is rotation-invariant, so this is a harmless no-op there.
   */
  setFacing(radians: number): void {
    this.rotation = radians;
  }

  /** Advance the looping strip on the render clock (the fallback dot has nothing to animate). */
  update(dtMs: number): void {
    if (!this.billboard || !this.clip) return;
    this.elapsedMs += dtMs;
    const loop = this.clip;
    const frame = Math.floor(this.elapsedMs / loop.frameIntervalMs) % loop.frames.length;
    const f = loop.frames[frame]!;
    this.billboard.texture = f.texture;
    this.billboard.pivot.set(f.cx, f.cy);
  }

  /** Tear down the bolt (it expired, hit, or otherwise left the world). */
  dispose(): void {
    this.destroy({ children: true });
  }
}
